import { useEffect, useState } from 'react';

import { Button, CircularProgress, Tooltip } from '@mui/material';

import { makeAuthenticatedApiCall } from '../../../apiHelper';

interface SendProcurementRequestButtonProps {
  dealId: number;
  pipeline?: 'a' | 'b';
  onSuccess?: () => void;
}

interface CerfaStatusResponseA {
  hasCerfaPdf: boolean;
  emailSent: boolean;
  filesComplete: boolean;
}

interface CerfaStatusResponseB {
  hasCessionPdf: boolean;
  hasPurchasePdf: boolean;
  bothPdfsReady: boolean;
  emailSent: boolean;
  filesComplete: boolean;
}

interface SendProcurementResponse {
  success: boolean;
  message?: string;
}

const SendProcurementRequestButton: React.FC<SendProcurementRequestButtonProps> = ({
  dealId,
  pipeline = 'a',
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPipelineB = pipeline === 'b';

  const statusEndpoint = isPipelineB
    ? `/api/deals/${dealId}/pipeline-b/cerfa-status`
    : `/api/deals/${dealId}/cerfa-status`;

  const sendEndpoint = isPipelineB
    ? `/api/deals/${dealId}/pipeline-b/send-procurement-request`
    : `/api/deals/${dealId}/send-procurement-request`;

  // Check cerfa status
  const checkCerfaStatus = async () => {
    try {
      if (isPipelineB) {
        const response = await makeAuthenticatedApiCall<CerfaStatusResponseB>('get', statusEndpoint);
        if (response.data) {
          setIsReady(response.data.bothPdfsReady);
          setEmailSent(response.data.emailSent);
        }
      } else {
        const response = await makeAuthenticatedApiCall<CerfaStatusResponseA>('get', statusEndpoint);
        if (response.data) {
          setIsReady(response.data.hasCerfaPdf);
          setEmailSent(response.data.emailSent);
        }
      }
    } catch (err) {
      // Error checking cerfa status
    } finally {
      setChecking(false);
    }
  };

  // Check cerfa status on mount
  useEffect(() => {
    checkCerfaStatus();
  }, [dealId]);

  // Listen for cerfa PDF upload events
  useEffect(() => {
    const handleCerfaUploaded = () => {
      checkCerfaStatus();
    };

    // Pipeline A: listen for cession cerfa only
    // Pipeline B: listen for both cession + purchase cerfa
    window.addEventListener('cerfaPdfUploaded', handleCerfaUploaded);

    if (isPipelineB) {
      window.addEventListener('purchaseCerfaPdfUploaded', handleCerfaUploaded);
    }

    return () => {
      window.removeEventListener('cerfaPdfUploaded', handleCerfaUploaded);
      if (isPipelineB) {
        window.removeEventListener('purchaseCerfaPdfUploaded', handleCerfaUploaded);
      }
    };
  }, [dealId, isPipelineB]);

  const handleSendEmail = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await makeAuthenticatedApiCall<SendProcurementResponse>('post', sendEndpoint, {});

      if (response.data?.success) {
        setEmailSent(true);
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err: any) {
      console.error('Error sending procurement request email:', err);
      const errorMessage =
        err.response?.data?.message || err.response?.data?.error || "Erreur lors de l'envoi de l'email";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking status
  if (checking) {
    return (
      <Button variant="contained" className="dealStageActionsButton" disabled>
        <CircularProgress size={20} style={{ color: 'white', marginRight: 8 }} />
        Vérification...
      </Button>
    );
  }

  // Show success state if email was sent
  if (emailSent) {
    return (
      <Button
        variant="contained"
        className="dealStageActionsButton"
        sx={{
          backgroundColor: '#2e7d32 !important',
          pointerEvents: 'none',
        }}
        disabled
      >
        ✅ Email envoyé
      </Button>
    );
  }

  // Determine tooltip and button text based on pipeline
  const buttonText = isPipelineB ? '📧 Demander le dossier de vente' : "📧 Demander dossier d'achat";

  const tooltipMessage = (() => {
    if (!isReady) {
      return isPipelineB
        ? 'Les 2 certificats (cession + achat) doivent être générés'
        : "Vous devez d'abord générer le certificat de cession";
    }
    if (error) return error;
    return isPipelineB
      ? "Envoyer un email à l'agence pour demander le dossier de vente"
      : "Envoyer un email à l'agence pour demander le dossier d'achat";
  })();

  return (
    <Tooltip title={tooltipMessage} placement="top">
      <span>
        <Button
          variant="contained"
          onClick={handleSendEmail}
          disabled={loading || !isReady}
          className="dealStageActionsButton"
          sx={{
            color: error ? '#d32f2f' : 'inherit',
            opacity: !isReady ? 0.5 : 1,
          }}
        >
          {loading ? <CircularProgress size={24} style={{ color: 'white' }} /> : buttonText}
        </Button>
      </span>
    </Tooltip>
  );
};

export default SendProcurementRequestButton;
